import React, { useEffect, useState } from "react";
import { api } from "../utils/api";

const ROLE_LABEL = { director: "Directeur", admin: "Admin", user: "Inspecteur" };
const ROLE_BADGE = { director: "badge-priority", admin: "badge-progress", user: "badge-routine" };

export default function UsersPage() {
  const [users, setUsers] = useState([]);
  const [pending, setPending] = useState([]);
  const [toast, setToast] = useState("");
  const [changePwd, setChangePwd] = useState(null);
  const [newPwd, setNewPwd] = useState("");
  const me = api.getCurrentUser();

  useEffect(() => { reload(); }, []);

  async function reload() {
    try {
      const [u, p] = await Promise.all([api.getUsers(), api.getPendingUsers()]);
      setUsers(u.filter(x => x.is_approved));
      setPending(p);
    } catch (e) { console.error(e); }
  }
  function showToast(msg) { setToast(msg); setTimeout(() => setToast(""), 3000); }

  // RANK: super_director=4, director=3, super_admin=2, admin=1, user=0
  function rankOf(u) {
    if (u.role === "director") return u.is_super_director ? 4 : 3;
    if (u.role === "admin") return u.is_super_admin ? 2 : 1;
    return 0;
  }
  const myRank = rankOf(me || {});
  const isDirector = me?.role === "director";

  function canActOn(u) {
    if (u.id === me?.id) return false;
    if (u.is_super_director) return false;
    return rankOf(u) < myRank;
  }
  // Roles I can assign: only if I outrank. Director assigns any; super_admin assigns admin/user; admin assigns nothing (rank change not allowed for simple admin)
  function assignableRoles() {
    if (myRank >= 3) return ["user", "admin", "director"]; // director
    if (myRank >= 2) return ["user", "admin"];             // super admin
    return []; // simple admin cannot change roles
  }
  function canChangePassword(u) {
    // Only own password - nobody can change another person password
    return u.id === me?.id;
  }

  async function approveUser(id) { try { await api.approveUser(id); showToast("Approuve"); reload(); } catch(e){ showToast("Erreur: "+e.message);} }
  async function rejectUser(id) { if(!window.confirm("Refuser ?"))return; try { await api.deleteUser(id); showToast("Refuse"); reload(); } catch(e){ showToast("Erreur: "+e.message);} }
  async function changeRole(id, role) { try { await api.changeUserRole(id, role); showToast("Role modifie"); reload(); } catch(e){ showToast("Erreur: "+e.message); reload(); } }
  async function toggleDashboard(id) { try { const r=await api.toggleDashboardAccess(id); showToast(r.dashboard_access?"Dashboard accorde":"Dashboard retire"); reload(); } catch(e){ showToast("Erreur: "+e.message);} }
  async function toggleSuper(id) { try { const r=await api.toggleSuperAdmin(id); showToast(r.is_super_admin?"Super admin designe":"Super admin retire"); reload(); } catch(e){ showToast("Erreur: "+e.message);} }
  async function deleteUser(id) { if(!window.confirm("Desactiver ? (historique preserve)"))return; try { await api.deleteUser(id); showToast("Desactive"); reload(); } catch(e){ showToast("Erreur: "+e.message);} }
  async function savePassword() { if(!newPwd||newPwd.length<6){showToast("Min 6 caracteres");return;} try { await api.changePassword(changePwd.id,newPwd); setChangePwd(null); setNewPwd(""); showToast("Mot de passe modifie"); } catch(e){ showToast("Erreur: "+e.message);} }

  const directors = users.filter(u => u.role === "director");
  const admins = users.filter(u => u.role === "admin");
  const inspectors = users.filter(u => u.role === "user");

  function UserRow({ u }) {
    const isMe = u.id === me?.id;
    const canAct = canActOn(u);
    const roles = assignableRoles();
    const showRoleDropdown = canAct && roles.length > 0;

    return (
      <div style={{ display:"flex", alignItems:"center", gap:12, padding:"12px 16px", borderBottom:"1px solid var(--border)", flexWrap:"wrap", background:isMe?"rgba(232,184,75,.05)":"white" }}>
        <div style={{ width:40, height:40, borderRadius:"50%", background:u.role==="director"?"#fee2e2":u.role==="admin"?"#dbeafe":"#f1f5f9", display:"flex", alignItems:"center", justifyContent:"center", fontSize:16, fontWeight:700, flexShrink:0, color:u.role==="director"?"var(--red)":u.role==="admin"?"var(--blue)":"var(--muted)" }}>
          {(u.full_name||u.username||"U")[0].toUpperCase()}
        </div>
        <div style={{ flex:1, minWidth:120 }}>
          <div style={{ fontWeight:600, fontSize:14, display:"flex", alignItems:"center", gap:6, flexWrap:"wrap" }}>
            {u.full_name||u.username}
            {isMe && <span className="badge badge-done" style={{fontSize:10}}>Vous</span>}
            {u.is_super_director && <span style={{fontSize:10, background:"#fef3c7", color:"#b45309", padding:"1px 6px", borderRadius:6, fontWeight:700}}>SUPER DIR</span>}
            {u.is_super_admin && <span style={{fontSize:10, background:"#dbeafe", color:"#1d4ed8", padding:"1px 6px", borderRadius:6, fontWeight:700}}>SUPER ADMIN</span>}
          </div>
          <div style={{ fontSize:11, color:"var(--muted)", marginTop:2, display:"flex", gap:8, flexWrap:"wrap" }}>
            <span>@{u.username}</span>
            {u.phone && <span>📞 {u.phone}</span>}
            {u.email && <span>✉️ {u.email}</span>}
            {u.zone && <span>📍 {u.zone}</span>}
          </div>
        </div>

        {showRoleDropdown ? (
          <select value={u.role} onChange={e=>changeRole(u.id, e.target.value)} style={{ padding:"5px 8px", borderRadius:6, border:"1px solid var(--border)", fontSize:12 }}>
            {roles.map(r => <option key={r} value={r}>{ROLE_LABEL[r]}</option>)}
          </select>
        ) : (
          <span className={`badge ${ROLE_BADGE[u.role]}`}>{ROLE_LABEL[u.role]}</span>
        )}

        {/* Super admin toggle - director only, for admins */}
        {isDirector && u.role === "admin" && (
          <button onClick={()=>toggleSuper(u.id)}
            style={{ fontSize:11, padding:"4px 10px", borderRadius:6, fontWeight:600, cursor:"pointer",
              background: u.is_super_admin?"#dbeafe":"white",
              color: u.is_super_admin?"#1d4ed8":"var(--muted)",
              border: `1px solid ${u.is_super_admin?"#1d4ed8":"var(--border)"}` }}>
            {u.is_super_admin ? "⭐ Super admin" : "Designer super"}
          </button>
        )}

        {/* Dashboard toggle - director only, for admins */}
        {isDirector && u.role === "admin" && (
          <div style={{ display:"flex", alignItems:"center", gap:6 }}>
            <span style={{ fontSize:10, color:"var(--muted)" }}>DASH:</span>
            <button className={`btn btn-sm ${u.dashboard_access?"btn-green":"btn-outline"}`} onClick={()=>toggleDashboard(u.id)} style={{ fontSize:11, padding:"3px 8px" }}>
              {u.dashboard_access?"✅":"❌"}
            </button>
          </div>
        )}

        <div className="flex gap-2">
          {canChangePassword(u) && <button className="btn btn-outline btn-sm" title="Mot de passe" onClick={()=>{setChangePwd(u);setNewPwd("");}}>🔑</button>}
          {canAct && <button className="btn btn-danger btn-sm" onClick={()=>deleteUser(u.id)}>🗑️</button>}
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className="page-header">
        <h1>Utilisateurs</h1>
        <p>Gestion des acces — Les inspecteurs s inscrivent via la page de connexion</p>
      </div>

      {pending.length > 0 && (
        <div className="card" style={{ marginBottom:20, borderLeft:"4px solid var(--orange)" }}>
          <div style={{ display:"flex", alignItems:"center", gap:8, marginBottom:14 }}>
            <span style={{ fontSize:20 }}>🔔</span>
            <span style={{ fontWeight:700, color:"var(--orange)", fontSize:14 }}>{pending.length} inscription(s) en attente</span>
          </div>
          {pending.map(u => (
            <div key={u.id} style={{ display:"flex", alignItems:"center", gap:12, padding:"10px 0", borderBottom:"1px solid var(--border)", flexWrap:"wrap" }}>
              <div style={{ flex:1, minWidth:0 }}>
                <div style={{ fontWeight:600, fontSize:13 }}>{u.full_name||u.username}</div>
                <div style={{ fontSize:11, color:"var(--muted)", display:"flex", gap:10, flexWrap:"wrap", marginTop:2 }}>
                  <span>@{u.username}</span>
                  {u.phone && <span>📞 {u.phone}</span>}
                  {u.email && <span>✉️ {u.email}</span>}
                  {u.zone && <span>📍 {u.zone}</span>}
                </div>
              </div>
              <div className="flex gap-2">
                <button className="btn btn-green btn-sm" onClick={()=>approveUser(u.id)}>✅ Approuver</button>
                <button className="btn btn-danger btn-sm" onClick={()=>rejectUser(u.id)}>❌ Refuser</button>
              </div>
            </div>
          ))}
        </div>
      )}

      {[{title:"Directeurs",list:directors},{title:"Administrateurs",list:admins},{title:"Inspecteurs",list:inspectors}].filter(g=>g.list.length>0).map(group => (
        <div key={group.title} style={{ marginBottom:20 }}>
          <div style={{ fontWeight:700, fontSize:11, color:"var(--muted)", textTransform:"uppercase", letterSpacing:".08em", marginBottom:8, display:"flex", alignItems:"center", gap:8 }}>
            {group.title}
            <span style={{ background:"var(--bg)", borderRadius:10, padding:"1px 8px", fontSize:11 }}>{group.list.length}</span>
          </div>
          <div className="card" style={{ padding:0, overflow:"hidden" }}>
            {group.list.map(u => <UserRow key={u.id} u={u} />)}
          </div>
        </div>
      ))}

      {changePwd && (
        <div className="modal-overlay">
          <div className="modal">
            <h2>🔑 Changer le mot de passe</h2>
            <p style={{ fontSize:13, color:"var(--muted)", marginBottom:16 }}>Compte : <strong>{changePwd.full_name||changePwd.username}</strong></p>
            <div className="form-group" style={{ marginBottom:20 }}>
              <label>Nouveau mot de passe</label>
              <input type="password" value={newPwd} onChange={e=>setNewPwd(e.target.value)} placeholder="Min 6 caracteres" autoFocus />
            </div>
            <div className="flex gap-2">
              <button className="btn btn-outline" style={{flex:1, justifyContent:"center"}} onClick={()=>setChangePwd(null)}>Annuler</button>
              <button className="btn btn-primary" style={{flex:1, justifyContent:"center"}} onClick={savePassword}>Enregistrer</button>
            </div>
          </div>
        </div>
      )}

      {toast && <div className="toast">{toast}</div>}
    </div>
  );
}
